import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, CalendarClock, Users, ClipboardList, Lock, UserPlus, X, ShieldAlert, Siren, ChevronDown, LineChart, UserRound, Target, BookOpen, Brain, MessagesSquare, CalendarPlus } from "lucide-react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import useFeatures from "../../../hooks/useFeatures";
import CrisisButton from "../../../components/consultation/CrisisButton";
import RiskAssessmentSection, { emptyRisk, riskStatus } from "../../../components/consultation/RiskAssessmentSection";
import SafetyPlanSection from "../../../components/clinical/SafetyPlanSection";
import MeasuresPanel from "../../../components/clinical/MeasuresPanel";
import PatientProfilePanel from "../../../components/clinical/PatientProfilePanel";
import PatientOverviewPanel from "../../../components/clinical/PatientOverviewPanel";
import AssignToolModal from "../../../components/clinical/AssignToolModal";
import TreatmentPlanPanel from "../../../components/clinical/TreatmentPlanPanel";
import HomeworkPanel from "../../../components/clinical/HomeworkPanel";
import FormulationPanel from "../../../components/clinical/FormulationPanel";
import AskSupervisionModal from "../../../components/clinical/AskSupervisionModal";
import CrisisAlertCard from "../../../components/clinical/CrisisAlertCard";
import MoodCheckinsCard from "../../../components/clinical/MoodCheckinsCard";
import { jwtDecode } from "jwt-decode";
import api from "../../../api";
import { refreshPulse } from "../../../utils/pulse";
import { PATIENT_LANGUAGES } from "../../../components/PatientLanguageFields";

// One page per patient (backend clinical/record/record_routes.py). Clinical
// parts follow care-team access (clinical/access/clinical_access.py); receptionists
// see appointment times only. Tabs for risk, measures, plans and homework
// join as those features are switched on (record.sections).
const RISK_BADGE = { low: "bg-emerald-100 text-emerald-800", moderate: "bg-amber-100 text-amber-800", high: "bg-rose-100 text-rose-800" };
const RISK_LABEL = {
  none: "None", passive: "Passive", active_no_plan: "Active, no plan", active_plan_or_intent: "Active, with plan or intent",
  historical: "Historical only", recent: "Recent", some: "Some", concerning: "Concerning",
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const currentUserId = () => {
  try { return jwtDecode(JSON.parse(localStorage.getItem("auth") || "{}").token)?.sub?.user_id ?? null; } catch { return null; }
};
const isAdminRole = () => {
  try { return ["admin", "ops", "operations"].includes((jwtDecode(JSON.parse(localStorage.getItem("auth") || "{}").token)?.sub?.role || "").toLowerCase()); } catch { return false; }
};
const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function PatientRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const features = useFeatures();
  const [askSupervision, setAskSupervision] = useState(false);
  const [assignTool, setAssignTool] = useState(false);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  // ?tab=risk (from Today, the risk register, alerts) opens that tab directly.
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const location = useLocation();
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t) setTab(t);
  }, [location.search]);
  const [adding, setAdding] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [newMember, setNewMember] = useState({ user_id: "", role: "clinician" });
  const [riskInfo, setRiskInfo] = useState(null);
  const [newRisk, setNewRisk] = useState(null);
  const [savingRisk, setSavingRisk] = useState(false);
  const [shownAlerts, setShownAlerts] = useState(new Set()); // collapsed alerts the user opened

  const loadRisk = () => api.getPatientRisk(id).then((r) => setRiskInfo(r.data)).catch(() => setRiskInfo({ current: null, history: [] }));
  const [crisisHistory, setCrisisHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const loadCrisisHistory = () => api.listCrisisAlerts({ patient_id: id, status: "all" }).then((r) => setCrisisHistory(r.data || [])).catch(() => setCrisisHistory([]));
  useEffect(() => {
    if (tab !== "risk" || !record) return;
    if (record.sections.includes("risk") && !riskInfo) loadRisk();
    if (record.sections.includes("crisis")) loadCrisisHistory();
  }, [tab, record]); // eslint-disable-line

  const saveRisk = async () => {
    setSavingRisk(true);
    try {
      await api.recordPatientRisk(id, newRisk);
      toast.success("Risk assessment saved");
      setNewRisk(null);
      loadRisk();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the assessment.");
    } finally {
      setSavingRisk(false);
    }
  };

  const acknowledgeAlert = async (a) => {
    try { await api.acknowledgeCrisisAlert(a.id); toast.success("Acknowledged"); load(); refreshPulse(); }
    catch (e) { toast.error(e?.response?.data?.error || "Couldn't acknowledge."); }
  };
  const resolveAlert = async (a, note) => {
    try { await api.resolveCrisisAlert(a.id, note || ""); toast.success("Alert closed"); load(); refreshPulse(); }
    catch (e) { toast.error(e?.response?.data?.error || "Couldn't close the alert."); }
  };

  const load = () =>
    api.getPatientRecord(id)
      .then((r) => setRecord(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "This patient wasn't found." : e?.response?.data?.error || "Couldn't load this patient."));

  useEffect(() => { if (features.ready && features.has("clinical_record")) load(); }, [id, features.ready]); // eslint-disable-line
  // The crisis ribbon's "Open" while already on this record reloads it.
  useEffect(() => {
    const onChange = (e) => { if (String(e.detail?.patientId) === String(id)) load(); };
    window.addEventListener("crisis-alerts-changed", onChange);
    return () => window.removeEventListener("crisis-alerts-changed", onChange);
  }, [id]); // eslint-disable-line

  const openAdd = async () => {
    setAdding(true);
    try {
      const res = await api.fetchDoctors({ limit: 100 });
      setDoctors((res.data?.data || []).filter((d) => d.is_active !== false));
    } catch { setDoctors([]); }
  };

  const addMember = async () => {
    if (!newMember.user_id) return;
    try {
      const res = await api.addCareTeamMember(id, { user_id: Number(newMember.user_id), role: newMember.role });
      setRecord((r) => ({ ...r, care_team: res.data }));
      setAdding(false);
      setNewMember({ user_id: "", role: "clinician" });
      toast.success("Added to the care team");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't add them.");
    }
  };

  const removeMember = async (m) => {
    try {
      const res = await api.removeCareTeamMember(id, m.id);
      setRecord((r) => ({ ...r, care_team: res.data }));
      toast.success(`${m.name} removed from the care team`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't remove them.");
    }
  };

  if (features.ready && !features.has("clinical_record")) {
    return (
      <DashboardLayout>
        <div className="p-8 text-secondary-600">The patient record page isn't switched on for your organisation yet.</div>
      </DashboardLayout>
    );
  }
  if (error) return <DashboardLayout><div className="p-8 text-red-600">{error}</div></DashboardLayout>;
  if (!record) return <DashboardLayout><div className="p-8 text-secondary-500">Loading…</div></DashboardLayout>;

  const { patient } = record;
  // An alert stays open on top only while it needs *this* person: nobody has
  // picked it up yet (and they didn't raise it), or they're the one handling
  // it. The rest collapse into one line so the record isn't pushed down.
  const me = currentUserId();
  const needsMe = (a) => (a.status === "open" && a.raised_by_user_id !== me) || (a.status === "acknowledged" && a.acknowledged_by === me);
  const collapsedAlerts = (record.open_alerts || []).filter((a) => !needsMe(a));
  const tabs = [
    { key: "overview", label: "Overview", icon: ClipboardList },
    { key: "profile", label: "Profile", icon: UserRound },
    { key: "sessions", label: "Sessions & notes", icon: CalendarClock },
    { key: "team", label: "Care team", icon: Users },
  ];
  if (["risk", "crisis", "safety_plan"].some((k) => record.sections.includes(k))) tabs.splice(2, 0, { key: "risk", label: "Risk & safety", icon: ShieldAlert });
  if (record.sections.includes("treatment_plan")) tabs.splice(tabs.length - 1, 0, { key: "plan", label: "Treatment plan", icon: Target });
  if (record.sections.includes("measures")) tabs.splice(tabs.length - 1, 0, { key: "measures", label: "Measures", icon: LineChart });
  if (record.sections.includes("toolkit")) tabs.splice(tabs.length - 1, 0, { key: "homework", label: "Homework", icon: BookOpen });
  if (record.sections.includes("formulation")) tabs.splice(tabs.length - 1, 0, { key: "formulation", label: "Formulation", icon: Brain });
  // Mood check-ins sit on the Measures tab; their own tab only when measures are off.
  if (record.sections.includes("checkins") && !record.sections.includes("measures")) tabs.splice(tabs.length - 1, 0, { key: "measures", label: "Mood", icon: LineChart });
  // A tab this person can't see (e.g. ?tab=risk for an admin without clinical access) falls back to Overview.
  const shownTab = tabs.some((t) => t.key === tab) ? tab : "overview";
  const onTeam = new Set(record.care_team.map((m) => m.user_id));

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <button type="button" onClick={() => navigate("/patient")} className="inline-flex items-center gap-1.5 text-sm text-secondary-500 hover:text-primary-600">
          <ArrowLeft className="w-4 h-4" /> Patients
        </button>

        <div className="rounded-2xl bg-white border border-secondary-100 shadow-sm p-5 md:p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-14 h-14 shrink-0 rounded-2xl bg-primary-100 text-primary-700 font-bold text-xl flex items-center justify-center">
              {(patient.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-secondary-900 break-words">{patient.name}</h1>
                {record.current_risk && (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${RISK_BADGE[record.current_risk.level]}`}>{record.current_risk.level} risk</span>
                )}
              </div>
              <p className="text-sm text-secondary-500">
                {[patient.age != null ? `${patient.age} yrs${patient.dob_is_estimated ? " (approx.)" : ""}` : null, patient.gender, patient.pronouns, patient.contact,
                  patient.language && patient.language !== "en-US" ? `Prefers ${(PATIENT_LANGUAGES.find(([c]) => c === patient.language) || [null, patient.language])[1].split(" - ")[0]}` : null].filter(Boolean).join(" · ")}
              </p>
              {(record.counts.no_shows > 0 || record.counts.late_cancels > 0) && (
                <p className="mt-0.5 text-xs font-semibold text-amber-700">
                  {[record.counts.no_shows > 0 && `${record.counts.no_shows} no-show${record.counts.no_shows === 1 ? "" : "s"}`,
                    record.counts.late_cancels > 0 && `${record.counts.late_cancels} late cancellation${record.counts.late_cancels === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
            {record.next_appointment && (
              <div className="rounded-xl bg-secondary-50 px-4 py-2.5 text-sm">
                <p className="text-xs text-secondary-500">Next session</p>
                <p className="font-semibold text-secondary-800">{fmtDate(record.next_appointment.date)}, {record.next_appointment.time} · {record.next_appointment.doctor_name}</p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-secondary-100 pt-4">
            {features.has("crisis") && <CrisisButton patientId={patient.id} patientName={patient.name} onRaised={load} />}
            {features.has("supervision") && record.can_view_clinical && (
              <button type="button" onClick={() => setAskSupervision(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-2 text-xs font-bold text-primary-700 hover:bg-primary-50">
                <MessagesSquare className="w-3.5 h-3.5" /> Ask for supervision
              </button>
            )}
            {record.sections.includes("toolkit") && (
              <button type="button" onClick={() => setAssignTool(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-2 text-xs font-bold text-primary-700 hover:bg-primary-50">
                <BookOpen className="w-3.5 h-3.5" /> Assign homework
              </button>
            )}
            {patient.contact && !String(patient.contact).startsWith("auto_") && (
              <button type="button" onClick={() => navigate("/appointment-new", { state: { newPatientPhone: patient.contact } })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-2 text-xs font-bold text-primary-700 hover:bg-primary-50">
                <CalendarPlus className="w-3.5 h-3.5" /> Book a session
              </button>
            )}
            <div className="ml-auto flex items-center divide-x divide-secondary-200 text-sm">
              {[["Appointments", record.counts.appointments],
                ...(record.can_view_clinical ? [["Consultations", record.counts.consultations ?? 0], ["Documents", record.counts.documents ?? 0]] : [])].map(([label, n]) => (
                <div key={label} className="flex items-baseline gap-1.5 px-3 first:pl-0 last:pr-0">
                  <span className="font-bold text-secondary-900">{n}</span>
                  <span className="text-xs text-secondary-500">{label}</span>
                </div>
              ))}
            </div>
          </div>
          {askSupervision && <AskSupervisionModal patientId={patient.id} patientName={patient.name} onClose={() => setAskSupervision(false)}
            sessions={record.sessions.filter((x) => x.consultation_id).slice(0, 6)} />}
          {assignTool && <AssignToolModal patientId={patient.id} patientName={patient.name} onClose={() => setAssignTool(false)} onAssigned={() => setAssignTool(false)} />}
        </div>

        {!record.can_view_clinical && (
          <div className="flex items-start gap-2 rounded-xl border border-secondary-200 bg-secondary-50 p-3 text-sm text-secondary-700">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" />
            You can see this patient's appointments{record.current_risk ? " and current risk level" : ""}. Clinical notes, including risk assessments, are only open to their care team.
          </div>
        )}

        {record.open_alerts?.filter((a) => needsMe(a) || shownAlerts.has(a.id)).map((a) => a.raised_by_user_id === currentUserId() && a.acknowledged_by !== currentUserId() ? (
          // The person who raised it only needs to know where it stands - the
          // responder contacts the patient and closes it. (When there was nobody
          // else to tell, the raiser is the handler and gets the full card.)
          <div key={a.id} className="flex items-start gap-3 rounded-xl border border-rose-200 bg-white p-4" role="status">
            <Siren className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-gray-900">You raised a crisis alert at {fmtDateTime(a.created_at)}</p>
              <p className="text-gray-600">
                {a.status === "acknowledged"
                  ? <><strong>{a.acknowledged_by_name || "A colleague"}</strong> is handling it and will close it when it's done.</>
                  : "The care team has been alerted. Waiting for someone to respond - stay with the patient and follow the crisis protocol."}
              </p>
            </div>
          </div>
        ) : (
          <CrisisAlertCard key={a.id} alert={a} me={me} fmtDateTime={fmtDateTime}
            canClose={a.acknowledged_by === me || isAdminRole()}
            raisedLabel={a.raised_by_patient ? "The patient asked for help" : a.raised_by_user_id === me ? "Crisis recorded by you - nobody else in the clinic was alerted" : `Crisis alert raised by ${a.raised_by_name || "staff"}`}
            onAcknowledge={acknowledgeAlert} onResolve={resolveAlert} />
        ))}

        {collapsedAlerts.length > 0 && (
          <details className="rounded-xl border border-rose-200 bg-white">
            <summary className="cursor-pointer list-none flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
              <Siren className="w-4 h-4 text-rose-600" />
              <span className="font-semibold text-gray-800">
                {collapsedAlerts.length} other open crisis alert{collapsedAlerts.length > 1 ? "s" : ""}
              </span>
              <span className="text-gray-500">- already being handled, or raised by you</span>
              <span className="ml-auto text-xs font-semibold text-rose-700">Show</span>
            </summary>
            <ul className="divide-y divide-gray-100 border-t border-gray-100">
              {collapsedAlerts.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-sm">
                  <span className="font-medium text-gray-900">
                    {a.raised_by_patient ? "Patient asked for help" : a.raised_by_user_id === currentUserId() ? "Raised by you" : `Raised by ${a.raised_by_name || "staff"}`}
                  </span>
                  <span className="text-gray-500">· {fmtDateTime(a.created_at)}</span>
                  <span className="text-gray-600">· {a.status === "acknowledged" ? `${a.acknowledged_by_name || "A colleague"} is handling it` : "waiting for a response"}</span>
                  <button type="button" onClick={() => setShownAlerts((s) => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })}
                    className="ml-auto text-xs font-semibold text-rose-700 hover:underline">
                    {shownAlerts.has(a.id) ? "Hide details" : "Show details"}
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* Folder tabs - same look as the consultation pages' tab bar. */}
        <div className="flex items-end gap-1 px-1.5 pt-1.5 pb-0.5 overflow-x-auto bg-secondary-50 rounded-xl">
          <nav className="flex items-end gap-1 min-w-full sm:min-w-0" aria-label="Patient record">
            {tabs.map((t) => {
              const active = shownTab === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setTab(t.key)} aria-current={active ? "page" : undefined}
                  className={`relative flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-t-lg text-xs md:text-sm font-bold whitespace-nowrap border-2 border-b-0 transition-colors cursor-pointer ${active
                    ? "bg-white text-primary-700 border-primary-500 shadow-md"
                    : "bg-secondary-200 text-secondary-600 border-transparent hover:bg-secondary-300/70 hover:text-secondary-800"}`}>
                  <t.icon className={`w-4 h-4 shrink-0 ${active ? "text-primary-600" : "text-secondary-500"}`} /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>

        {shownTab === "overview" && record.can_view_clinical && (
          <PatientOverviewPanel patientId={patient.id} record={record} onOpenTab={setTab} />
        )}

        {shownTab === "overview" && (
          <div className="grid gap-4">
            <div className="rounded-xl bg-white border border-secondary-100 p-4">
              <p className="text-sm font-semibold text-secondary-800 mb-2">Care team</p>
              {record.care_team.length === 0 ? (
                <p className="text-sm text-secondary-500">No one yet. A doctor joins automatically when they're booked with or see this patient.</p>
              ) : (
                <p className="text-sm text-secondary-700">{record.care_team.map((m) => `${m.name}${m.role === "primary" ? " (primary)" : ""}`).join(", ")}</p>
              )}
            </div>
          </div>
        )}

        {shownTab === "sessions" && (
          <div className="rounded-xl bg-white border border-secondary-100 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-secondary-50 text-xs uppercase tracking-wide text-secondary-500">
                <tr><th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Clinician</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Status</th><th className="px-4 py-2.5" /></tr>
              </thead>
              <tbody>
                {record.sessions.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-500">No appointments yet.</td></tr>
                )}
                {record.sessions.map((s) => (
                  <tr key={s.appointment_id} className="border-t border-secondary-100">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(s.date)} · {s.time}</td>
                    <td className="px-4 py-2.5">{s.doctor_name}</td>
                    <td className="px-4 py-2.5">{s.appttype}{s.branch_name ? ` · ${s.branch_name}` : ""}</td>
                    <td className="px-4 py-2.5">{s.consultation_status === "completed" ? "Seen" : s.confirmation_status}</td>
                    <td className="px-4 py-2.5 text-right">
                      {s.consultation_id && (
                        <button type="button" onClick={() => navigate(`/consultation/${s.consultation_id}`)} className="text-primary-600 font-semibold hover:underline">Open notes</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {shownTab === "risk" && (
          <div className="space-y-4">
            {record.sections.includes("safety_plan") && (
              <SafetyPlanSection patientId={patient.id} patientName={patient.name} riskLevel={record.current_risk?.level} />
            )}
            {record.sections.includes("risk") && (<>
            {!newRisk ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white border border-secondary-100 p-4">
                <div>
                  <p className="text-xs text-secondary-500">Current risk</p>
                  {riskInfo?.current ? (
                    <p className="text-sm text-secondary-800">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize mr-2 ${RISK_BADGE[riskInfo.current.overall_risk]}`}>{riskInfo.current.overall_risk}</span>
                      {fmtDateTime(riskInfo.current.created_at)} · {riskInfo.current.assessed_by_name}
                    </p>
                  ) : <p className="text-sm text-secondary-500">{riskInfo ? "No assessment yet." : "Loading…"}</p>}
                </div>
                <button type="button" onClick={() => setNewRisk(emptyRisk())} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-2 hover:bg-primary-700">New assessment</button>
              </div>
            ) : (
              <div className="rounded-xl bg-white border border-secondary-100 p-4 space-y-3">
                <RiskAssessmentSection value={newRisk} onChange={setNewRisk} />
                <div className="flex gap-2">
                  <button type="button" onClick={saveRisk} disabled={savingRisk || riskStatus(newRisk) !== "complete"}
                    className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50">{savingRisk ? "Saving…" : "Save assessment"}</button>
                  <button type="button" onClick={() => setNewRisk(null)} className="rounded-lg border border-secondary-300 text-sm px-3 py-2">Cancel</button>
                </div>
              </div>
            )}
            {riskInfo?.history?.length > 0 && (
              <div className="rounded-xl bg-white border border-secondary-100 divide-y divide-secondary-100">
                {riskInfo.history.map((h) => (
                  <div key={h.id} className="p-4 text-sm space-y-1">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${RISK_BADGE[h.overall_risk]}`}>{h.overall_risk}</span>
                      <span className="text-secondary-600">{fmtDateTime(h.created_at)} · {h.assessed_by_name}{h.source === "note" ? " · in session notes" : ""}</span>
                      {h.consultation_id && <button type="button" onClick={() => navigate(`/consultation/${h.consultation_id}`)} className="text-primary-600 text-xs font-semibold hover:underline">Open notes</button>}
                    </p>
                    <p className="text-secondary-700">
                      Suicidal ideation: {RISK_LABEL[h.suicidal_ideation]} · Self-harm: {RISK_LABEL[h.self_harm]} · Risk to others: {RISK_LABEL[h.harm_to_others]}
                    </p>
                    {h.protective_factors && <p className="text-secondary-600"><span className="font-semibold">Protective factors:</span> {h.protective_factors}</p>}
                    {h.action_taken && <p className="text-secondary-600"><span className="font-semibold">Action taken:</span> {h.action_taken}</p>}
                  </div>
                ))}
              </div>
            )}
            </>)}

            {record.sections.includes("crisis") && (
              <div className="rounded-xl bg-white border border-secondary-100">
                <button type="button" onClick={() => setHistoryOpen((o) => !o)} aria-expanded={historyOpen}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-secondary-800 text-left ${historyOpen ? "border-b border-secondary-100" : ""}`}>
                  <Siren className="w-4 h-4 text-rose-600" /> Crisis alerts history
                  {crisisHistory?.length > 0 && <span className="text-xs font-normal text-secondary-500">({crisisHistory.length})</span>}
                  <ChevronDown className={`ml-auto w-4 h-4 text-secondary-500 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
                </button>
                {!historyOpen ? null : crisisHistory === null ? <p className="p-4 text-sm text-secondary-500">Loading…</p>
                  : crisisHistory.length === 0 ? <p className="p-4 text-sm text-secondary-500">No crisis alerts for this patient.</p> : (
                  <ul className="divide-y divide-secondary-100">
                    {crisisHistory.map((a) => (
                      <li key={a.id} className="p-4 text-sm space-y-1.5">
                        <p className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-secondary-900">{a.raised_by_patient ? "Patient pressed \"Get help\" in their app" : `Raised by ${a.raised_by_name || "staff"}`}</span>
                          <span className="text-secondary-500">· {fmtDateTime(a.created_at)}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${a.status === "resolved" ? "bg-emerald-100 text-emerald-800" : a.status === "acknowledged" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}`}>
                            {a.status === "resolved" ? "Closed" : a.status === "acknowledged" ? "Being handled" : "Waiting for response"}
                          </span>
                        </p>
                        {a.summary && <p className="text-secondary-700">{a.summary}</p>}
                        <ol className="ml-1 border-l-2 border-secondary-200 pl-3 space-y-0.5 text-xs text-secondary-600">
                          {a.acknowledged_at && <li>Picked up by <strong>{a.acknowledged_by_name || "staff"}</strong> · {fmtDateTime(a.acknowledged_at)}</li>}
                          {a.resolved_at && <li>Closed by <strong>{a.resolved_by_name || "staff"}</strong> · {fmtDateTime(a.resolved_at)}</li>}
                          {a.resolution_note && <li className="text-secondary-800"><span className="font-semibold">What was done:</span> {a.resolution_note}</li>}
                          {a.acknowledged_at && <li>Response time: {Math.max(1, Math.round((new Date(a.acknowledged_at) - new Date(a.created_at)) / 60000))} min</li>}
                        </ol>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {shownTab === "measures" && (
          <div className="space-y-4">
            {record.sections.includes("checkins") && <MoodCheckinsCard patientId={patient.id} />}
            {record.sections.includes("measures") && <MeasuresPanel patientId={patient.id} />}
          </div>
        )}

        {shownTab === "plan" && <TreatmentPlanPanel patientId={patient.id} patientName={patient.name} />}
        {shownTab === "homework" && <HomeworkPanel patientId={patient.id} patientName={patient.name} />}
        {shownTab === "formulation" && <FormulationPanel patientId={patient.id} />}

        {shownTab === "profile" && <PatientProfilePanel patientId={patient.id} canViewClinical={record.can_view_clinical} />}

        {shownTab === "team" && (
          <div className="rounded-xl bg-white border border-secondary-100 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-secondary-600">The clinicians who can open this patient's clinical record.</p>
              {record.can_manage_care_team && !adding && (
                <button type="button" onClick={openAdd} className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-2 hover:bg-primary-700">
                  <UserPlus className="w-4 h-4" /> Add clinician
                </button>
              )}
            </div>
            {adding && (
              <div className="flex flex-wrap items-end gap-3 rounded-lg bg-secondary-50 p-3">
                <label className="normal-case font-sans tracking-normal text-sm">
                  <span className="block text-xs text-secondary-600 mb-1">Clinician</span>
                  <select value={newMember.user_id} onChange={(e) => setNewMember({ ...newMember, user_id: e.target.value })} className="rounded-md border border-secondary-300 p-2 text-sm min-w-[220px]">
                    <option value="">Choose…</option>
                    {doctors.filter((d) => !onTeam.has(d.doctor_id)).map((d) => <option key={d.doctor_id} value={d.doctor_id}>{d.name}{d.specialized ? ` · ${d.specialized}` : ""}</option>)}
                  </select>
                </label>
                <label className="normal-case font-sans tracking-normal text-sm">
                  <span className="block text-xs text-secondary-600 mb-1">Role</span>
                  <select value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} className="rounded-md border border-secondary-300 p-2 text-sm">
                    <option value="clinician">Clinician</option>
                    <option value="primary">Primary clinician</option>
                  </select>
                </label>
                <button type="button" onClick={addMember} disabled={!newMember.user_id} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50">Add</button>
                <button type="button" onClick={() => setAdding(false)} className="rounded-lg border border-secondary-300 text-sm px-3 py-2">Cancel</button>
              </div>
            )}
            <ul className="divide-y divide-secondary-100">
              {record.care_team.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div>
                    <p className="text-sm font-semibold text-secondary-800">{m.name} <span className="text-xs font-medium text-secondary-500">· {m.role === "primary" ? "Primary clinician" : m.role === "supervisor" ? "Supervisor" : "Clinician"}</span></p>
                    <p className="text-xs text-secondary-500">{m.specialization || ""}{m.added_automatically ? " · added automatically" : ""}</p>
                  </div>
                  {record.can_manage_care_team && record.care_team.length > 1 && (
                    <button type="button" onClick={() => removeMember(m)} className="p-1.5 rounded-full text-secondary-400 hover:text-red-600 hover:bg-red-50" aria-label={`Remove ${m.name}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
